// ============================================================================
// Moving stock without a delivery — two writes, one component each (§15a):
//   * TransferWindow  — location → location (dock → storage unit, or Josh's home
//                       → the new Houston office when the lease closes: one
//                       transfer, and the history stays intact).
//   * PickupWindow    — clinic → location (leftover stock at a clinic that ended).
// A transfer carries its average cost with it.
// ⛔ Each draft resets on open and after a successful save.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { fetchLocationStock, pickupStock, transferStock, type LocationStockRow, type OfficeRow, type ProductRow } from './api';
import { onHandText, sourceChoices } from './stock-sources';
import { useStockLoad } from './use-stock-data';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';

interface MoveDraft {
  from: string;
  to: string;
  supplyId: string;
  qty: string;
  notes: string;
}

export function TransferWindow({
  open,
  onClose,
  offices,
  products,
  defaultFrom,
}: {
  open: boolean;
  onClose: () => void;
  offices: OfficeRow[];
  products: ProductRow[];
  defaultFrom?: string | null;
}) {
  const blank = (): MoveDraft => ({ from: defaultFrom ?? '', to: '', supplyId: products[0]?.supply_id ?? '', qty: '', notes: '' });
  const [d, setD] = useState<MoveDraft>(blank);
  useEffect(() => {
    if (open) setD(blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const n = parseQty(d.qty);
  const unit = products.find((p) => p.supply_id === d.supplyId)?.unit_label ?? null;
  const canSave = !!d.from && !!d.to && d.from !== d.to && !!d.supplyId && n != null && n > 0;

  async function save() {
    if (n == null) return;
    await transferStock({ from: d.from, to: d.to, supplyId: d.supplyId, qty: n, notes: d.notes || null });
    setD(blank());
    onClose();
  }

  return (
    <StockWindow open={open} title="Move stock between locations" canSave={canSave} saveLabel="Save move" onCancel={onClose} onSave={save}>
      <div className="grid gap-3 sm:grid-cols-2">
        <OfficeSelect label="From" value={d.from} onChange={(v) => setD((x) => ({ ...x, from: v }))} offices={offices} />
        <OfficeSelect
          label="To"
          value={d.to}
          onChange={(v) => setD((x) => ({ ...x, to: v }))}
          offices={offices.filter((o) => o.stock_role === 'stock')}
        />
      </div>
      <ProductSelect value={d.supplyId} onChange={(v) => setD((x) => ({ ...x, supplyId: v }))} products={products} />
      <QtyField label={unit === 'tabs' ? 'Tabs' : 'Gallons'} value={d.qty} onChange={(v) => setD((x) => ({ ...x, qty: v }))} step={unit === 'tabs' ? 5 : 1} />
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Anything worth saying (optional)</span>
        <input className={FIELD_CLASS} value={d.notes} onChange={(e) => setD((x) => ({ ...x, notes: e.target.value }))} />
      </label>
    </StockWindow>
  );
}

export function PickupWindow({
  open,
  onClose,
  facilityId,
  title,
  offices,
  products,
  defaultOffice,
}: {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  title: string;
  offices: OfficeRow[];
  products: ProductRow[];
  defaultOffice: string | null;
}) {
  const blank = (): MoveDraft => ({ from: '', to: defaultOffice ?? '', supplyId: products[0]?.supply_id ?? '', qty: '', notes: '' });
  const [d, setD] = useState<MoveDraft>(blank);
  useEffect(() => {
    if (open) setD(blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Back to one of the stock locations in this clinic's region, with what each holds (TCSD-0388).
  const stockQ = useStockLoad(() => (open ? fetchLocationStock() : Promise.resolve([])), [open], [] as LocationStockRow[]);
  const choices = useMemo(() => sourceChoices(offices, defaultOffice, stockQ.data), [offices, defaultOffice, stockQ.data]);

  const n = parseQty(d.qty);
  const product = products.find((p) => p.supply_id === d.supplyId);
  const unit = product?.unit_label ?? null;
  const canSave = !!d.to && choices.some((c) => c.office_id === d.to) && !!d.supplyId && n != null && n > 0;

  async function save() {
    if (n == null) return;
    await pickupStock({ facilityId, officeId: d.to, supplyId: d.supplyId, qty: n, notes: d.notes || null });
    setD(blank());
    onClose();
  }

  return (
    <StockWindow open={open} title="Pick up leftover stock" subtitle={title} canSave={canSave} saveLabel="Save pickup" onCancel={onClose} onSave={save}>
      <ProductSelect value={d.supplyId} onChange={(v) => setD((x) => ({ ...x, supplyId: v }))} products={products} />
      <QtyField label={unit === 'tabs' ? 'Tabs' : 'Gallons'} value={d.qty} onChange={(v) => setD((x) => ({ ...x, qty: v }))} step={unit === 'tabs' ? 5 : 0.5} />
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Back to</span>
        <select className={FIELD_CLASS} value={d.to} onChange={(e) => setD((x) => ({ ...x, to: e.target.value }))}>
          <option value="">— pick —</option>
          {choices.map((c) => (
            <option key={c.office_id} value={c.office_id}>
              {c.office_name} — {product ? onHandText(c, [product]) : ''} on hand
              {c.isDefault ? ' (this clinic’s)' : ''}
            </option>
          ))}
        </select>
      </label>
    </StockWindow>
  );
}

function OfficeSelect({ label, value, onChange, offices }: { label: string; value: string; onChange: (v: string) => void; offices: OfficeRow[] }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tcs-text-muted">{label}</span>
      <select className={FIELD_CLASS} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— pick —</option>
        {offices.map((o) => (
          <option key={o.office_id} value={o.office_id}>
            {o.office_name}
            {o.stock_role === 'receive_only' ? ' (receives freight)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProductSelect({ value, onChange, products }: { value: string; onChange: (v: string) => void; products: ProductRow[] }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tcs-text-muted">Product</span>
      <select className={FIELD_CLASS} value={value} onChange={(e) => onChange(e.target.value)}>
        {products.map((p) => (
          <option key={p.supply_id} value={p.supply_id ?? ''}>
            {p.unit_label === 'tabs' ? 'Stearns tabs' : 'Micro-Chem Plus, gallons'}
          </option>
        ))}
      </select>
    </label>
  );
}
